import React from 'react';
import clsx from 'clsx';
import {enumToValues} from '@netx/core-next/utils/enum';
import {NumberField} from '@netx/core-components/NumberField';
import {SelectField} from '@netx/core-components/SelectField';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {SearchSubTypeLabel} from '../../../../locales';
import {CommonRuleEditorProps} from '../../../types';
import {
	FileAspectType,
	FileAspectSubType,
	FileAspectOrientationValue,
} from '../../constants';
import {FileAspectTypeLabel} from '../../locales';
import {
	getFileAspectSubTypeLabel,
	getFileAspectTypeLabel,
	getOrientationLabel,
} from '../../utils';
import {
	fileAspectRuleEditorClasses,
	getFileAspectRuleEditorUtilityClass,
	FileAspectRuleEditorClasses,
	FILE_ASPECT_RULE_EDITOR_CLASS_NAME,
} from './fileAspectRuleEditorClasses';

export type FileAspectRuleEditorOwnerState = WithClasses<
	FileAspectRuleEditorClasses,
	{}
>;

const useUtilityClasses = (ownerState: FileAspectRuleEditorOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getFileAspectRuleEditorUtilityClass, classes);
};

const FileAspectRuleEditorRoot = styled('div', {
	name: FILE_ASPECT_RULE_EDITOR_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	display: 'flex',
	flex: '1 1 auto',
	flexDirection: 'column',
}));

const FileAspectEditorCriteria = styled('div', {
	name: FILE_ASPECT_RULE_EDITOR_CLASS_NAME,
	slot: 'Criteria',
})(({theme}) => ({
	display: 'flex',
	flex: '0 0 auto',
	'& > * + *': {
		marginLeft: theme.spacing(1),
	},
}));

const FileAspectEditorValues = styled('div', {
	name: FILE_ASPECT_RULE_EDITOR_CLASS_NAME,
	slot: 'Values',
})(({theme}) => ({
	display: 'flex',
	flex: '0 0 auto',
	marginTop: theme.spacing(2),
	'& > * + *': {
		marginLeft: theme.spacing(1),
	},
}));

const ORIENTATION_OPTIONS = enumToValues(FileAspectOrientationValue);

export type FileAspectRuleEditorProps = WithClasses<
	FileAspectRuleEditorClasses,
	Pick<CommonRuleEditorProps, 'validationErrors'> & {
		elementSubType1: FileAspectType;
		elementSubType1Options: FileAspectType[];
		elementSubType2: FileAspectSubType;
		elementSubType2Options: FileAspectSubType[];
		elementValue1: number | FileAspectOrientationValue;
		onSubType1Change: (subType1: FileAspectType) => void;
		onSubType2Change: (subType2: FileAspectSubType) => void;
		onValue1Change: (value: number | FileAspectOrientationValue) => void;
	}
>;

const FileAspectRuleEditorInner = ({
	classes: _classes,
	className,
	elementSubType1,
	elementSubType1Options,
	elementSubType2,
	elementSubType2Options,
	elementValue1,
	validationErrors,
	onSubType1Change,
	onSubType2Change,
	onValue1Change,
}: FileAspectRuleEditorProps) => {
	const ownerState: FileAspectRuleEditorOwnerState = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	return (
		<FileAspectRuleEditorRoot className={clsx(classes.root, className)}>
			<FileAspectEditorCriteria>
				<SelectField<FileAspectType, false, true, false>
					data-testid="file-aspect-rule-sub-type-1"
					disableClearable
					label={SearchSubTypeLabel.MATCH_TYPE}
					name={'rule-sub-type-1'}
					options={elementSubType1Options}
					value={elementSubType1}
					getOptionLabel={getFileAspectTypeLabel}
					onChange={(event, value) => onSubType1Change(value)}
				/>

				<SelectField<FileAspectSubType, false, true, false>
					data-testid="file-aspect-rule-sub-type-2"
					disableClearable
					disabled={elementSubType2Options.length === 1}
					name={'rule-sub-type-2'}
					options={elementSubType2Options}
					value={elementSubType2}
					getOptionLabel={getFileAspectSubTypeLabel}
					onChange={(event, value) => onSubType2Change(value)}
				/>
			</FileAspectEditorCriteria>

			<FileAspectEditorValues>
				{elementSubType1 === FileAspectType.ORIENTATION ? (
					<SelectField<FileAspectOrientationValue, false, true, false>
						data-testid="file-aspect-rule-value-1"
						disableClearable
						error={validationErrors?.elementValue1?.message}
						options={ORIENTATION_OPTIONS}
						label={FileAspectTypeLabel[elementSubType1]}
						name={'rule-value-1'}
						value={elementValue1 as number}
						getOptionLabel={getOrientationLabel}
						onChange={(event, value) => onValue1Change(value)}
					/>
				) : (
					<NumberField
						data-testid="file-aspect-rule-value-1"
						error={validationErrors?.elementValue1?.message}
						label={FileAspectTypeLabel[elementSubType1]}
						name="rule-value-1"
						required
						value={parseInt(elementValue1.toString()) || 0}
						onChange={(event, value) => onValue1Change(parseInt(value))}
					/>
				)}
			</FileAspectEditorValues>
		</FileAspectRuleEditorRoot>
	);
};

export const FileAspectRuleEditorPrimitive = FileAspectRuleEditorInner;

export const FileAspectRuleEditor = FileAspectRuleEditorPrimitive;

export default FileAspectRuleEditor;

export type FileAspectRuleEditorGenericProps = WithClasses<
	FileAspectRuleEditorClasses,
	CommonRuleEditorProps
>;

export const FileAspectRuleEditorGenericPrimitive = ({
	elementValue1,
	elementValue2,
	elementValue3,
	onValue2Change,
	onValue3Change,
	...props
}: FileAspectRuleEditorGenericProps) => {
	return (
		<FileAspectRuleEditor
			{...props}
			elementValue1={parseInt(elementValue1.toString())}
		/>
	);
};

export const FileAspectRuleEditorGeneric = FileAspectRuleEditorGenericPrimitive;
